import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';

import type { ConnectorPromoConfig } from '../connector-promo-config-types.ts';
import { normalizeFields } from '../utils/analytics.ts';

import { ConnectorIconPlaceholder } from './ConnectorIconPlaceholder.tsx';
import { ConnectorPromoCardBase } from './ConnectorPromoCardBase.tsx';

const messages = defineMessages({
	heading: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.postConnectPromo.heading',
		defaultMessage: 'Your team’s search results could be better',
		description: 'Heading for the post-connect connector promo card',
	},
	body: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.postConnectPromo.body',
		defaultMessage:
			'You’ve connected {connectedAppName}—now search across {nextConnectorName} from Confluence.',
		description:
			'Body text for the post-connect promo card with interpolation for connected and next app names',
	},
	setupLabel: {
		id: 'experiment-twcg-1489-cc-3p-connector-onboarding.postConnectPromo.setupLabel',
		defaultMessage: 'Set up',
		description: 'Primary CTA button label on the post-connect promo card',
	},
});

type ConnectorPostConnectPromoCardProps = Pick<
	ConnectorPromoConfig,
	'connectedAppName' | 'nextRecommendedConnector' | 'onSetupConnector'
> & {
	onDismissPromo: () => void;
	testId?: string;
};

export const ConnectorPostConnectPromoCard = ({
	connectedAppName,
	nextRecommendedConnector,
	onSetupConnector,
	onDismissPromo,
	testId = 'connector-post-connect-promo-card',
}: ConnectorPostConnectPromoCardProps): React.JSX.Element | null => {
	const { formatMessage } = useIntl();
	const { createEvent } = useConfluenceAnalyticsEvents();

	const handlePrimaryClick = useCallback(() => {
		if (!nextRecommendedConnector) {
			return;
		}
		createEvent({
			type: 'sendUIEvent',
			data: {
				source: 'ConnectorPostConnectPromoCard',
				action: 'clicked',
				actionSubject: 'button',
				attributes: normalizeFields({
					connectorKey: nextRecommendedConnector.key,
					container: 'ConnectorPromoNavSearch',
					testId: `${testId}--primary-cta`,
				}),
			},
		}).fire();
		onSetupConnector(nextRecommendedConnector.key);
	}, [nextRecommendedConnector, onSetupConnector, createEvent, testId]);

	const handleDismiss = useCallback(() => {
		createEvent({
			type: 'sendUIEvent',
			data: {
				source: 'ConnectorPostConnectPromoCard',
				action: 'clicked',
				actionSubject: 'button',
				attributes: normalizeFields({
					connectorKey: nextRecommendedConnector?.key,
					container: 'ConnectorPromoNavSearch',
					testId: `${testId}--dismiss`,
				}),
			},
		}).fire();
		onDismissPromo();
	}, [nextRecommendedConnector, onDismissPromo, createEvent, testId]);

	if (!nextRecommendedConnector) {
		return null;
	}

	return (
		<ConnectorPromoCardBase
			icon={<ConnectorIconPlaceholder connector={nextRecommendedConnector} size={24} />}
			heading={formatMessage(messages.heading)}
			body={formatMessage(messages.body, {
				connectedAppName: connectedAppName || '',
				nextConnectorName: nextRecommendedConnector.displayName,
			})}
			primaryCtaLabel={formatMessage(messages.setupLabel)}
			onPrimaryClick={handlePrimaryClick}
			onDismiss={handleDismiss}
			testId={testId}
		/>
	);
};
